import type { MemoryProposeResult } from "@/lib/customer-memory";

const TOKEN_KEY = "c456_wecom_sidebar_token";

export type SidebarBootstrap = {
  corp_id: string | null;
  agent_id: string | null;
  oauth_redirect_url: string | null;
  domain_status: string;
};

export type SidebarSession = {
  access_token: string;
  expires_in: number;
  wecom_userid: string;
  user_id: number | null;
  account_id: number;
};

export type SidebarJsapiConfig = {
  corp_id: string;
  agent_id: number | string;
  timestamp: number | string;
  nonce_str: string;
  signature: string;
  agent_signature: string;
};

export type SidebarJourney = {
  bound: boolean;
  journey_name?: string | null;
  node_title?: string | null;
  next_touch_at?: string | null;
  workspace_url?: string | null;
  error?: string;
};

export type SidebarContact = {
  id: number;
  name: string | null;
  avatar_url: string | null;
  follow_userids: string[];
  follow_user_names: string[];
  workspace_url: string | null;
  memory_summary: string | null;
};

export type SidebarCurrentContact = {
  found: boolean;
  contact?: SidebarContact;
  journey?: SidebarJourney;
};

export class WecomSidebarApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "WecomSidebarApiError";
  }
}

export function readSidebarToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeSidebarToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearSidebarToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

export function stripHash(url: string): string {
  const i = url.indexOf("#");
  return i >= 0 ? url.slice(0, i) : url;
}

export function oauthAuthorizeUrl(corpId: string, redirectUri: string): string {
  const params = new URLSearchParams({
    appid: corpId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "snsapi_base",
    state: "c456",
  });
  return `https://open.weixin.qq.com/connect/oauth2/authorize?${params.toString()}#wechat_redirect`;
}

async function sidebarFetch<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw new WecomSidebarApiError(response.status, extractCode(payload), extractMessage(payload) || `请求失败（${response.status}）`);
  }
  return payload as T;
}

export function fetchBootstrap() {
  return sidebarFetch<SidebarBootstrap>("/api/v1/wecom_sidebar/bootstrap");
}

export function createSidebarSession(code: string) {
  return sidebarFetch<SidebarSession>("/api/v1/wecom_sidebar/sessions", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export function fetchJsapiConfig(token: string, url: string) {
  return sidebarFetch<SidebarJsapiConfig>(
    "/api/v1/wecom_sidebar/jsapi_config",
    { method: "POST", body: JSON.stringify({ url }) },
    token,
  );
}

export function fetchCurrentContact(token: string, externalUserid: string) {
  const qs = new URLSearchParams({ external_userid: externalUserid });
  return sidebarFetch<SidebarCurrentContact>(
    `/api/v1/wecom_sidebar/current_contact?${qs.toString()}`,
    {},
    token,
  );
}

export function proposeSidebarMemory(
  token: string,
  externalUserid: string,
  facts: string,
) {
  return sidebarFetch<MemoryProposeResult>(
    "/api/v1/wecom_sidebar/current_contact/memory/propose",
    {
      method: "POST",
      body: JSON.stringify({
        external_userid: externalUserid,
        facts,
      }),
    },
    token,
  );
}

export function updateSidebarMemory(
  token: string,
  externalUserid: string,
  memorySummary: string,
) {
  return sidebarFetch<{ memory_summary: string | null }>(
    "/api/v1/wecom_sidebar/current_contact/memory",
    {
      method: "PATCH",
      body: JSON.stringify({
        external_userid: externalUserid,
        memory_summary: memorySummary,
      }),
    },
    token,
  );
}

function extractMessage(payload: unknown): string | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "object" &&
    payload.error !== null &&
    "message" in payload.error &&
    typeof payload.error.message === "string"
  ) {
    return payload.error.message;
  }
  return undefined;
}

function extractCode(payload: unknown): string | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "object" &&
    payload.error !== null &&
    "code" in payload.error &&
    typeof payload.error.code === "string"
  ) {
    return payload.error.code;
  }
  return undefined;
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}
